import { types as api } from '@mesa-labs/mesa-api';
import { StatusColors } from '@mesa-labs/mesa-ui';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import styled from 'styled-components';

import {
  actionItemStatusColorKey, actionItemStatusLabel, byCreatedDesc, formatAssigneeEmail, isOpenActionItem,
} from '../../../../utils/vendorActionItems';
import { ActionItemWrites } from '../hooks';
import MoveToMenu from './MoveToMenu';
import { ErrorText, LinkButton } from './styles';

const Panel = styled.div`
  background: white;
  border: 1px solid #EAECF0;
  border-radius: 12px;
  box-shadow: 0 20px 24px -4px rgba(16, 24, 40, 0.08), 0 8px 8px -4px rgba(16, 24, 40, 0.03);
  color: #101828;
  font-size: 13px;
  overflow: hidden;
  width: 480px;
`;

const Header = styled.div`
  align-items: center;
  background: #F9FAFB;
  border-bottom: 1px solid #EAECF0;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
`;

// Baseline, not center: the board link lines up with the label text rather than the tall count.
const TitleGroup = styled.div`
  align-items: baseline;
  display: flex;
  gap: 12px;
`;

const Count = styled.span`
  font-size: 20px;
  font-weight: 700;
  margin-right: 6px;
`;

const CountLabel = styled.span`
  color: ${(p) => p.theme.colors.Gray600};
`;

// A real link in a new tab, so the board opens beside whatever page the menu was opened on.
const BoardLink = styled.a`
  color: #6941C6;
  font-size: 12px;
  font-weight: 600;

  &:hover { text-decoration: underline; }
`;

// Capped to the viewport below the nav and the popup's header so the list's end stays reachable on short screens.
const Scroll = styled.div`
  max-height: min(520px, calc(100vh - 180px));
  overflow-y: auto;
`;

const Empty = styled.div`
  color: ${(p) => p.theme.colors.Gray500};
  padding: 32px 16px;
  text-align: center;
`;

const Row = styled.div<{ $expanded: boolean }>`
  background: ${(p) => (p.$expanded ? '#F9F5FF' : 'white')};
  border-left: 3px solid ${(p) => (p.$expanded ? '#9E77ED' : 'transparent')};
  padding: 8px 16px 8px 13px;

  &:hover { background: ${(p) => (p.$expanded ? '#F9F5FF' : '#F9FAFB')}; }
`;

const RowToggle = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex: 1;
  min-width: 0;
  padding: 0;
  text-align: left;
`;

// Top-aligned so the ⋯ menu stays beside the first line when an expanded title wraps.
const RowTop = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 8px;
`;

const RowText = styled.div`
  min-width: 0;
`;

// Top-aligned so the dot stays beside the first line when an expanded title wraps.
const TitleLine = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 8px;
`;

const statusColors = (colorKey: string): Record<string, string> => StatusColors[colorKey] ?? StatusColors.default;

// The status badge's colors in a circle: its text color as the border, filled a third of the way from the badge's soft
// background toward it so the fill reads at this size.
const StatusDot = styled.span<{ $colorKey: string }>`
  background: ${(p) => `color-mix(in srgb, ${statusColors(p.$colorKey).backgroundColor} 67%, ${statusColors(p.$colorKey).color})`};
  border: 2px solid ${(p) => statusColors(p.$colorKey).color};
  border-radius: 999px;
  box-sizing: border-box;
  flex-shrink: 0;
  height: 12px;
  margin-top: 3px; /* centers on the title's 18px first line */
  width: 12px;
`;

// One truncated line until the row expands, then the whole title.
const ItemTitle = styled.div<{ $expanded: boolean }>`
  color: #101828;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  min-width: 0;
  ${(p) => (p.$expanded ? 'overflow-wrap: anywhere;' : 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap;')}
`;

const Eyebrow = styled.div`
  color: ${(p) => p.theme.colors.Gray500};
  font-size: 11px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SmallButton = styled.button<{ $primary?: boolean }>`
  background: ${(p) => (p.$primary ? '#7F56D9' : 'white')};
  border: 1px solid ${(p) => (p.$primary ? '#7F56D9' : p.theme.colors.Gray300)};
  border-radius: 6px;
  color: ${(p) => (p.$primary ? 'white' : p.theme.colors.Gray700)};
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  white-space: nowrap;

  &:disabled { cursor: default; opacity: 0.5; }
`;

const Expanded = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 4px;
`;

const Description = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  color: ${(p) => p.theme.colors.Gray600};
  display: -webkit-box;
  overflow: hidden;
  white-space: pre-wrap;
`;

const LatestNote = styled.div`
  background: white;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  color: #344054;
  padding: 8px 10px;
  white-space: pre-wrap;
`;

const Meta = styled.div`
  color: #98A2B3;
  font-size: 11px;
  margin-top: 2px;
`;

const NoteBox = styled.textarea`
  border: 1px solid ${(p) => p.theme.colors.Gray300};
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  min-height: 56px;
  padding: 8px;
  resize: vertical;
`;

const Bar = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const Error = styled(ErrorText)`
  align-items: center;
  border-bottom: 1px solid #FEE4E2;
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
`;

const DoneTray = styled.div`
  background: #F6FEF9;
  border-top: 1px solid #D1FADF;
  padding: 8px 16px;
`;

const DoneRow = styled.div`
  align-items: center;
  color: #067647;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  padding: 3px 0;
`;

const relative = (iso: string): string => DateTime.fromISO(iso).toRelative({ style: 'short' }) ?? '';

// An item closed from the list, kept so it can be reopened to the status it came from.
type Closed = { readonly item: api.VendorActionItemResponse; readonly from: api.VendorActionItemStatus };

type Props = {
  // The viewer's open items.
  items: readonly api.VendorActionItemResponse[];
  canWrite: boolean;
  writes: ActionItemWrites;
  onOpenDetail: (item: api.VendorActionItemResponse) => void;
  onCreate: () => void;
};

// The nav menu's list of the viewer's open Action Items, newest created first so rows hold their place while being
// worked (status changes and Notes don't reorder them). A row's ⋯ menu changes its status; the row expands for its
// description, latest Note and a Note box. Items closed here wait in a "Done" tray with Undo until the menu closes.
export default function QuickList({
  items, canWrite, writes, onOpenDetail, onCreate,
}: Props): React.ReactElement {
  const [expandedId, setExpandedId] = useState<string | undefined>();
  const [draft, setDraft] = useState('');
  const [closed, setClosed] = useState<readonly Closed[]>([]);

  const closedIds = new Set(closed.map(({ item }) => item.id));
  const open = items.filter((item) => isOpenActionItem(item) && !closedIds.has(item.id)).sort(byCreatedDesc);

  const toggleRow = (id: string) => {
    setDraft('');
    setExpandedId((current) => (current === id ? undefined : id));
  };

  const move = async (item: api.VendorActionItemResponse, to: api.VendorActionItemStatus) => {
    const moved = await writes.update(item.vendorId, item.id, { status: to });
    if (moved && !api.isOpenVendorActionItemStatus(to)) setClosed((c) => [...c, { item, from: item.status }]);
  };

  const undo = async ({ item, from }: Closed) => {
    if (await writes.update(item.vendorId, item.id, { status: from })) setClosed((c) => c.filter((x) => x.item.id !== item.id));
  };

  // Keep the draft when the save fails so the Note isn't lost.
  const addNote = async (item: api.VendorActionItemResponse) => {
    const note = draft.trim();
    if (note && await writes.addNote(item.vendorId, item.id, note)) setDraft('');
  };

  const renderRow = (item: api.VendorActionItemResponse) => {
    const expanded = expandedId === item.id;
    return (
      <Row key={item.id} $expanded={expanded}>
        <RowTop>
          <RowToggle aria-expanded={expanded} onClick={() => toggleRow(item.id)}>
            <RowText>
              <TitleLine>
                <StatusDot
                  $colorKey={actionItemStatusColorKey(item.status)}
                  role="img"
                  aria-label={actionItemStatusLabel(item.status)}
                  title={actionItemStatusLabel(item.status)}
                />
                <ItemTitle $expanded={expanded} title={expanded ? undefined : item.title}>{item.title}</ItemTitle>
              </TitleLine>
              <Eyebrow>{`${item.vendorName ?? ''} · Updated ${relative(item.lastActivityAt)}`}</Eyebrow>
            </RowText>
          </RowToggle>
          {canWrite && <MoveToMenu status={item.status} onMove={(to) => move(item, to)} />}
        </RowTop>
        {expanded && (
          <Expanded>
            {item.description && <Description>{item.description}</Description>}
            {item.latestNote && (
              <LatestNote>
                {item.latestNote.note}
                <Meta>
                  {`${formatAssigneeEmail(item.latestNote.createdBy)} · ${relative(item.latestNote.createdAt)}`}
                  {` · ${item.noteCount} ${item.noteCount === 1 ? 'comment' : 'comments'}`}
                </Meta>
              </LatestNote>
            )}
            {canWrite && (
              <NoteBox
                aria-label="Add a comment"
                placeholder="Add a comment… (⌘↵ to save)"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote(item); }}
              />
            )}
            <Bar>
              <LinkButton onClick={() => onOpenDetail(item)}>Open full details</LinkButton>
              {canWrite && <SmallButton $primary disabled={!draft.trim()} onClick={() => addNote(item)}>Add comment</SmallButton>}
            </Bar>
          </Expanded>
        )}
      </Row>
    );
  };

  return (
    <Panel>
      <Header>
        <TitleGroup>
          <div>
            <Count>{open.length}</Count>
            <CountLabel>open and assigned to you</CountLabel>
          </div>
          <BoardLink href="/action-items" target="_blank" rel="noopener noreferrer">Open board ↗</BoardLink>
        </TitleGroup>
        {canWrite && <SmallButton $primary onClick={onCreate}>+ New</SmallButton>}
      </Header>
      {writes.error && (
        <Error role="alert">
          {writes.error}
          <LinkButton onClick={writes.clearError}>Dismiss</LinkButton>
        </Error>
      )}
      <Scroll>
        {open.length === 0 && closed.length === 0 && <Empty>All clear — nothing open for you.</Empty>}
        {open.map(renderRow)}
      </Scroll>
      {closed.length > 0 && (
        <DoneTray>
          {closed.map((entry) => (
            <DoneRow key={entry.item.id}>
              <span>{`✓ ${entry.item.title}`}</span>
              <LinkButton onClick={() => undo(entry)}>Undo</LinkButton>
            </DoneRow>
          ))}
        </DoneTray>
      )}
    </Panel>
  );
}
